"use client";

/**
 * EventMyPositions — wraps MyPositions with all the event-scoped props
 * (allowedConditionIds, marketMetaByConditionId, sell-click handler)
 * derived from the event payload, so we can mount it both inside
 * EventSidebar (desktop) and inline on mobile without duplicating the
 * derivation logic in two places.
 */

import { useTradingModalStore } from "@/stores/useTradingModalStore";
import MyPositions from "@/app/market/[slug]/components/MyPositions";
import type { EventData } from "@/hooks/useEventQuery";
import type { Market, Source, GetLivePriceFn } from "./types";

interface Props {
  selectedMarket: Market;
  activeMarkets: Market[];
  source: Source;
  isKalshi: boolean;
  eventData: EventData;
  getLivePrice: GetLivePriceFn;
  /** Tailwind classes applied to the root wrapper. Use to gate visibility
   *  per breakpoint (e.g. "lg:hidden" for mobile-only mounting). */
  className?: string;
  /** Mirror of EventSidebar.onRequestSell. When provided, the Sell
   *  button drives the TradingPanel state through the parent instead
   *  of opening a per-source modal. */
  onRequestSell?: (position: any) => void;
}

export default function EventMyPositions({
  selectedMarket,
  activeMarkets,
  source,
  isKalshi,
  eventData,
  getLivePrice,
  className = "",
  onRequestSell,
}: Props) {
  const { openPolymarket, openPredict } = useTradingModalStore();

  const eventConditionIds = (activeMarkets || [])
    .map((m) => m.conditionId)
    .filter((c): c is string => !!c);
  const eventMarketSlugs = (activeMarkets || [])
    .map((m) => m.slug)
    .filter((s): s is string => !!s);

  const marketMetaByConditionId: Record<string, { image?: string | null; label: string }> = {};
  for (const m of activeMarkets || []) {
    const label = m.groupItemTitle || m.question || m.slug || '';
    if (m.conditionId) {
      marketMetaByConditionId[m.conditionId.toLowerCase()] = {
        image: (m as any).image || eventData?.image || null,
        label,
      };
    }
  }

  const kalshiTicker = isKalshi
    ? String(
        (selectedMarket as any).ticker ||
          selectedMarket.slug ||
          selectedMarket.conditionId ||
          selectedMarket.id ||
          ''
      )
        .replace(/^ks-/i, '')
        .toUpperCase()
    : undefined;

  const handleSellClick = (position: any) => {
    // Parent-driven panel mode: route the click through onRequestSell
    // so the event page's TradingPanel switches to SELL with the
    // position seeded. Modal is the legacy fallback.
    if (onRequestSell) {
      onRequestSell(position);
      return;
    }
    const outcomes = selectedMarket.outcomes || ['Yes', 'No'];
    const posOutcome = (position.outcome || '').toLowerCase();
    const realIndex = outcomes.findIndex((o: string) => o.toLowerCase() === posOutcome);
    const outcomeIndex = realIndex >= 0 ? realIndex : (position.outcomeIndex ?? 0);
    const outcome = outcomeIndex === 0 ? 'YES' : 'NO';
    const [liveYes] = getLivePrice(selectedMarket);
    const price = outcomeIndex === 0 ? (liveYes ?? 0.5) : (1 - (liveYes ?? 0.5));
    // Real labels for both sides — always derive from the API outcomes
    // pair so the modal renders Over/Under, Up/Down, sports team names.
    const sideLabel = outcomes?.[outcomeIndex];
    const altSideLabel = outcomes?.[outcomeIndex === 0 ? 1 : 0];

    if (source === 'predict') {
      openPredict({
        marketId: selectedMarket.id,
        marketSlug: selectedMarket.slug || '',
        outcome: outcome as 'YES' | 'NO',
        outcomePrice: price,
        marketQuestion: selectedMarket.question,
        marketImage: selectedMarket.image,
        altOutcomePrice: 1 - price,
        outcomes,
        actualOutcome: sideLabel,
        altActualOutcome: altSideLabel,
        mode: 'sell',
        positionShares: position.shares,
        positionAvgPrice: position.avgPrice,
        positionAmountRaw: position.amountRaw,
      });
    } else if (source === 'kalshi') {
      const { openKalshi } = useTradingModalStore.getState();
      openKalshi({
        marketTicker: kalshiTicker || selectedMarket.slug || selectedMarket.conditionId,
        dflowTicker: kalshiTicker,
        marketTitle: selectedMarket.question,
        outcome: outcome.toLowerCase() as 'yes' | 'no',
        currentPrice: price,
        altPrice: 1 - price,
        yesMint: (selectedMarket as any).yesMint,
        noMint: (selectedMarket as any).noMint,
        mode: 'sell',
        positionSize: position.shares,
        avgEntryPrice: position.avgPrice,
      });
    } else {
      openPolymarket({
        marketSlug: selectedMarket.slug,
        outcome: outcome as 'YES' | 'NO',
        outcomePrice: price,
        marketQuestion: selectedMarket.question,
        marketImage: selectedMarket.image,
        altOutcomePrice: 1 - price,
        outcomes,
        actualOutcome: sideLabel,
        altActualOutcome: altSideLabel,
        clobTokenIds: selectedMarket.clobTokenIds || [],
        conditionId: selectedMarket.conditionId,
        mode: 'sell',
        positionShares: position.shares,
        positionAvgPrice: position.avgPrice,
      });
    }
  };

  return (
    <div className={className}>
      <MyPositions
        conditionId={selectedMarket.conditionId}
        acceptingOrders={selectedMarket.acceptingOrders}
        marketQuestion={selectedMarket.question}
        marketImage={selectedMarket.image || undefined}
        outcomes={selectedMarket.outcomes}
        source={source}
        kalshiTicker={kalshiTicker}
        predictMarketId={source === 'predict' ? selectedMarket.id : undefined}
        onSellClick={handleSellClick}
        allowedConditionIds={source === 'polymarket' ? eventConditionIds : undefined}
        allowedMarketSlugs={source !== 'polymarket' ? eventMarketSlugs : undefined}
        marketMetaByConditionId={marketMetaByConditionId}
      />
    </div>
  );
}
